import React, { lazy, Suspense } from "react";
import {
	Routes,
	Route,
	Navigate,
	Outlet,
	createBrowserRouter,
} from "react-router-dom";
import * as COMMONS from "common/common";
import { AnimatePresence } from "framer-motion";
import { ErrorBoundaryComponent, TopProgressBar } from "components";
import { SurveyProvider } from "components/client/SurveyContext";
import { Spin } from "antd";
import { useIsFetching } from "@tanstack/react-query";
import BaseSpin from "./components/common/BaseSpin";
import Stamps from "views/Admin/Stamps";

const MinimalLayout = lazy(() => import("./layouts/Minimal"));
const AdminMainLayout = lazy(() => import("./layouts/AdminMain"));
const ClientMainLayout = lazy(() => import("./layouts/ClientMain"));
// const AdminCustomersView = lazy(() => import("./views/Admin/Customers"))
const AdminLotteryListView = lazy(
	() => import("./views/Admin/Lottery/LotteryList"),
);
const AdminLotteryDetailView = lazy(
	() => import("./views/Admin/Lottery/LotteryDetail"),
);
const AdminCouponsView = lazy(() => import("./views/Admin/Coupons"));
const AdminLoginView = lazy(() => import("./views/Admin/Login"));
// const AdminHomeView = lazy(() => import("./views/Admin/Home"))
const AdminHomeV2View = lazy(() => import("./views/Admin/HomeV2"));
const AdminCategoriesView = lazy(() => import("./views/Admin/Categories"));
// const AdminOccasionVersionOccasionsView = lazy(() =>
//   import("./views/Admin/OccasionVersion/Occasions")
// )
const AdminOccasionVersionOccasionsDetailView = lazy(
	() => import("./views/Admin/OccasionVersion/OccasionsDetail"),
);
// const AdminCategoryVersionCategoryDetailView = lazy(() =>
//   import("./views/Admin/CategoryVersion/CategoryDetail")
// )
const EventDetailView = lazy(() => import("./views/Admin/EventDetail"));
const InflowSourceAnalysisView = lazy(() => import("./views/Admin/InflowSourceAnalysis"));

const AdminCampaignsView = lazy(() => import("./views/Admin/Campaigns"));

const AdminCampaignDetaillView = lazy(
	() => import("./views/Admin/CampaignDetail/CampaignDetail"),
);
const AdminMembersView = lazy(() => import("./views/Admin/Members"));
const AdminAudiencesView = lazy(() => import("./views/Admin/Audiences"));
const AdminSettingsView = lazy(() => import("./views/Admin/Settings"));
const AdminSystemSettingView = lazy(
	() => import("./views/Admin/Settings/system"),
);
const AdminEventSettingView = lazy(
	() => import("./views/Admin/Settings/event"),
);
const AdminRichmenuSettingView = lazy(
	() => import("./views/Admin/Settings/richmenu"),
);
const AdminNotificationSettingView = lazy(
	() => import("./views/Admin/Settings/notification"),
);
const AdminMemebershipCardSettingView = lazy(
	() => import("./views/Admin/Settings/membership_card"),
);
const AdminSurveysView = lazy(() => import("./views/Admin/Surveys"));
const AdminSurveysAddEdit = lazy(() => import("./views/Admin/Surveys/AddEdit"));
const AdminStatisticsSurvey = lazy(
	() => import("./views/Admin/Surveys/Statistics"),
);
const AdminSurveyTemplateView = lazy(
	() => import("./views/Admin/SurveyTemplate"),
);

const ClientLotteryListView = lazy(
	() => import("./views/Client/Lottery/LotteryList"),
);
const ClientLotteryDetailView = lazy(
	() => import("./views/Client/Lottery/LotteryDetail"),
);
const ClientLoginView = lazy(() => import("./views/Client/Login"));
const ClientRegisterView = lazy(() => import("./views/Client/Register"));
const ClientSurveyView = lazy(() => import("./views/Client/surveys"));
const ClientSurveyThankView = lazy(() => import("./views/Client/SurveyThank"));

const ClientCampaignView = lazy(() => import("./views/Client/Campaign"));
const ClientProfileUpdateView = lazy(
	() => import("./views/Client/ProfileUpdate"),
);
const ClientMembershipView = lazy(() => import("./views/Client/Membership"));
const ClientCategoriesView = lazy(() => import("./views/Client/Categories"));
const ClientCategoryDetailView = lazy(
	() => import("./views/Client/CategoryDetail"),
);

const ClientCampaignsView = lazy(() => import("./views/Client/Campaigns"));
const ClientCampaignRegistrationsView = lazy(
	() => import("./views/Client/Campaigns/CampaignRegistrations"),
);
const ClientCampaignDetailView = lazy(
	() => import("./views/Client/Campaigns/CampaignDetail"),
);

const ClientVersionOccurrencesView = lazy(
	() => import("./views/Client/ClientVersionOccurrencesView"),
);
const ClientVersionReservationView = lazy(
	() => import("./views/Client/ClientVersionReservationView"),
);

// const ClientCategoryVersionOccurrencesView = lazy(() =>
//   import("./views/Client/CategoryVersion/Occurrences")
// )
// const ClientOccasionVersionOccasionDetailView = lazy(() =>
//   import("./views/Client/OccasionVersion/OccasionDetail")
// )
// const ClientCategoryVersionReservationView = lazy(() =>
//   import("./views/Client/CategoryVersion/Reservation")
// )
// const ClientOccasionVersionReservationView = lazy(() =>
//   import("./views/Client/OccasionVersion/Reservation")
// )
const ClientRegistrationsView = lazy(
	() => import("./views/Client/Registrations"),
);
const ClientHistoryCampaignRegistrationsView = lazy(
	() => import("./views/Client/HistoryCampaignRegistrations"),
);
const ClientRegistrationDetailView = lazy(
	() => import("./views/Client/RegistrationDetail"),
);

const ClientCampaignRegistrationDetailView = lazy(
	() => import("./views/Client/Campaigns/RegistrationDetail"),
);

const LineAccessView = lazy(() => import("./views/LineAccess"));
const LineFriendView = lazy(() => import("./views/LineFriend"));
const PermissionErrorView = lazy(() => import("./views/PermissionError"));
const NotFoundView = lazy(() => import("./views/NotFound"));

const Router = createBrowserRouter([
	{
		path: "*",
		element: (
			<ErrorBoundaryComponent>
				<Suspense
					fallback={
						<div className="flex flex-col w-full min-h-full">
							<TopProgressBar />
							<BaseSpin />
						</div>
					}
				>
					<AnimatePresence mode="wait">
						<Routes>
							<Route
								path="/"
								element={<Navigate replace to={COMMONS.ADMIN_LOGIN_ROUTE} />}
							/>
							<Route
								path={COMMONS.ADMIN_LOGIN_ROUTE}
								element={
									<MinimalLayout>
										<AdminLoginView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_HOME_ROUTE}
								element={
									<AdminMainLayout>
										{/* <AdminHomeView /> */}
										<AdminHomeV2View />
									</AdminMainLayout>
								}
							/>
							{/* <Route
		  path={COMMONS.ADMIN_CUSTOMERS_ROUTE}
		  element={
			<AdminMainLayout>
			  <AdminCustomersView />
			</AdminMainLayout>
		  }
		/> */}
							<Route
								path={COMMONS.ADMIN_COUPONS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminCouponsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_LOTTERY_ROUTE}
								element={
									<AdminMainLayout>
										<AdminLotteryListView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_LOTTERY_ROUTE}/:lotteryId`}
								element={
									<AdminMainLayout>
										<AdminLotteryDetailView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_CATEGORIES_ROUTE}
								element={
									<AdminMainLayout>
										<AdminCategoriesView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_CATEGORIES_ROUTE}/:categoryId`}
								element={
									<AdminMainLayout>
										<EventDetailView />
										{/* {COMMONS.IS_CATEGORY_VERSION ? (
				<AdminCategoryVersionCategoryDetailView />
			  ) : (
				<AdminOccasionVersionOccasionsView />
			  )} */}
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_CAMPAIGN_ROUTE}/:campaignId`}
								element={
									<AdminMainLayout>
										<AdminCampaignDetaillView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_CATEGORIES_ROUTE}/:categoryId/:occasionId`}
								element={
									<AdminMainLayout>
										{/* {COMMONS.IS_CATEGORY_VERSION ? (
				<NotFoundView />
			  ) : ( */}
										<AdminOccasionVersionOccasionsDetailView />
										{/* )} */}
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_MEMBERS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminMembersView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_INFLOW_SOURCE_ANALYSIS_ROUTE}
								element={
									<AdminMainLayout>
										<InflowSourceAnalysisView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_CAMPAIGN_ROUTE}
								element={
									<AdminMainLayout>
										{/* <AdminMembersView /> */}
										<AdminCampaignsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_AUDIENCES_ROUTE}
								element={
									<AdminMainLayout>
										<AdminAudiencesView />
									</AdminMainLayout>
								}
							/>
							{/* <Route
								path={COMMONS.ADMIN_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminSettingsView />
									</AdminMainLayout>
								}
							/> */}
							<Route
								path={COMMONS.ADMIN_RICHMENU_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminRichmenuSettingView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SYSTEM_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminSystemSettingView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_NOTIFICATION_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminNotificationSettingView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_EVENT_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminEventSettingView />
									</AdminMainLayout>
								}
							/>

							<Route
								path={COMMONS.ADMIN_CARD_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminMemebershipCardSettingView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SURVEY_ROUTE}
								element={
									<AdminMainLayout>
										<Outlet />
									</AdminMainLayout>
								}
							>
								<Route path="" index element={<AdminSurveysView />} />
								<Route path="add" element={<AdminSurveysAddEdit />} />
								<Route path=":surveyId" element={<AdminSurveysAddEdit />} />
							</Route>
							<Route
								path={`${COMMONS.ADMIN_SURVEY_ROUTE}/:surveyId/statistics`}
								element={
									<AdminMainLayout>
										<AdminStatisticsSurvey />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_SURVEY_ROUTE}/template/:surveyId`}
								element={
									<AdminMainLayout>
										<AdminSurveyTemplateView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.ADMIN_STAMPS_ROUTE}`}
								element={
									<AdminMainLayout>
										<Stamps />
									</AdminMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_LOGIN_ROUTE}/:liffId`}
								element={
									<MinimalLayout>
										<SurveyProvider>
											<ClientLoginView />
										</SurveyProvider>
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_REGISTER_ROUTE}/:liffId`}
								element={
									<ClientMainLayout withoutBottomMenuBar>
										<ClientRegisterView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_LOTTERY_ROUTE}/:liffId`}
								element={
									<ClientMainLayout withoutBottomMenuBar>
										<ClientLotteryListView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_LOTTERY_ROUTE}/:liffId/:lotteryId`}
								element={
									<ClientMainLayout withoutBottomMenuBar>
										<ClientLotteryDetailView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_SURVEY_RECORD_ROUTE}/:surveyId`}
								element={
									<MinimalLayout>
										<SurveyProvider>
											<ClientSurveyView />
										</SurveyProvider>
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_SURVEY_RECORD_ROUTE}/Thank`}
								element={
									<MinimalLayout>
										<SurveyProvider>
											<ClientSurveyThankView />
										</SurveyProvider>
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CAMPAIGN_ROUTE}/:liffId`}
								element={
									<MinimalLayout>
										<ClientCampaignView />
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_PROFILE_UPDATE_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientProfileUpdateView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_MEMBERSHIP_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientMembershipView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CATEGORIES_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientCategoriesView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CATEGORIES_ROUTE}/:liffId/:categoryId`}
								element={
									<ClientMainLayout>
										<ClientCategoryDetailView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CAMPAIGNS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientCampaignsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CAMPAIGNS_ROUTE}/:liffId/:campaignId`}
								element={
									<ClientMainLayout>
										<ClientCampaignDetailView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CAMPAIGNS_ROUTE}/:liffId/:campaignId/:occurrenceId`}
								element={
									<ClientMainLayout>
										<ClientCampaignRegistrationsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CATEGORIES_ROUTE}/:liffId/:categoryId/:occasionId`}
								element={
									<ClientMainLayout>
										<ClientVersionOccurrencesView />
										{/* {COMMONS.IS_CATEGORY_VERSION ? (
				<ClientCategoryVersionOccurrencesView />
			  ) : (
				<ClientOccasionVersionOccasionDetailView />
			  )} */}
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CATEGORIES_ROUTE}/:liffId/:categoryId/:occasionId/:occurrenceId`}
								element={
									<ClientMainLayout>
										<ClientVersionReservationView />
										{/* {COMMONS.IS_CATEGORY_VERSION ? (
				<ClientCategoryVersionReservationView />
			  ) : (
				<ClientOccasionVersionReservationView />
			  )} */}
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_REGISTRATIONS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientRegistrationsView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_CAMPAIGN_REGISTRATIONS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientHistoryCampaignRegistrationsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_REGISTRATION_DETAIL_ROUTE}/:liffId/:registrationId`}
								element={
									<ClientMainLayout>
										<ClientRegistrationDetailView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_CAMPAIGN_REGISTRATION_DETAIL_ROUTE}/:liffId/:registrationId/:campaignId`}
								element={
									<ClientMainLayout>
										<ClientCampaignRegistrationDetailView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={COMMONS.LINE_ACCESS_ROUTE}
								element={
									<MinimalLayout>
										<LineAccessView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.LINE_FRIEND_ROUTE}
								element={
									<MinimalLayout>
										<LineFriendView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.PERMISSION_ERROR_ROUTE}
								element={
									<MinimalLayout>
										<PermissionErrorView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.NOT_FOUND_ROUTE}
								element={
									<MinimalLayout>
										<NotFoundView />
									</MinimalLayout>
								}
							/>
							<Route
								path="*"
								element={<Navigate replace to={COMMONS.NOT_FOUND_ROUTE} />}
							/>
						</Routes>
					</AnimatePresence>
				</Suspense>
			</ErrorBoundaryComponent>
		),
	},
]);
export default Router;
